import { ExperimentOutlined, TeamOutlined, ToolOutlined } from "@ant-design/icons";
import amzLogo from "@images/amazon-logo.png";
import { login } from "@services/auth";
import { AUTH_PROVIDERS, AuthProvider } from "@services/env";
import { Button, Card, Image, message, Space, Spin, Tag, Typography } from "antd";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";

const { Title, Text } = Typography;

// ============================================================================
// Theme Tokens (aligned with MainHeader / project theme)
// ============================================================================

const Theme = {
  // Navy gradient backgrounds (from MainHeader)
  navyDark: "#0a1628",
  navyMid: "#1a2942",
  navyLight: "#0d1b2a",
  // Accent
  orange: "#ff9900",
  orangeLight: "#ffb84d",
  orangeHover: "#EC7211",
  orangeActive: "#EB5F07",
  // Surface
  bgPage: "#f0f2f5",
  white: "#ffffff",
  border: "#d5dbdb",
  borderLight: "rgba(255, 255, 255, 0.08)",
  // Text
  textPrimary: "#16191f",
  textSecondary: "#545b64",
  textTertiary: "#687078",
  textWhite: "rgba(255, 255, 255, 0.9)",
  textWhiteMuted: "rgba(255, 255, 255, 0.7)",
  // Shadows (from MainHeader)
  headerShadow: "0 2px 8px rgba(0, 0, 0, 0.15), 0 4px 16px rgba(0, 0, 0, 0.1)",
  cardShadow: "0 1px 3px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.05)",
};

// ============================================================================
// Styled Components
// ============================================================================

const LoginContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: ${Theme.bgPage};
`;

/** Top bar — mirrors the MainHeader gradient and height */
const TopBar = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: var(--header-height, 60px);
  background: linear-gradient(135deg, ${Theme.navyDark} 0%, ${Theme.navyMid} 50%, ${Theme.navyLight} 100%);
  display: flex;
  align-items: center;
  padding: 0 1.5rem;
  z-index: 1001;
  box-shadow: ${Theme.headerShadow};

  /* Orange bottom glow – same as MainHeader */
  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255, 153, 0, 0.3), transparent);
  }
`;

const TopBarLogo = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  gap: 0.5rem;

  .ant-image {
    display: flex;
    align-items: center;
  }

  span {
    color: ${Theme.orange};
    font-size: 1.25rem;
    font-weight: 600;
    letter-spacing: -0.01em;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  }
`;

const LoginCard = styled(Card)`
  width: 560px;
  border-radius: 12px;
  border: 1px solid ${Theme.border};
  box-shadow: ${Theme.cardShadow};
  overflow: hidden;

  .ant-card-body {
    padding: 0;
  }
`;

/** Card header matches the project navy gradient */
const CardHeader = styled.div`
  background: linear-gradient(135deg, ${Theme.navyDark} 0%, ${Theme.navyMid} 50%, ${Theme.navyLight} 100%);
  padding: 40px 40px 34px;
  text-align: center;
  position: relative;
`;

const CardBody = styled.div`
  padding: 32px 40px 40px;
`;

/** Orange accent divider between header and body */
const Divider = styled.div`
  height: 3px;
  background: linear-gradient(90deg, ${Theme.orange}, ${Theme.orangeLight});
`;

const ProviderButton = styled(Button)<{ $enabled?: boolean }>`
  width: 100%;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0 20px;
  border-radius: 8px;
  font-size: 15px;
  border: 1px solid ${props => (props.$enabled ? Theme.orange : Theme.border)};
  background: ${props => (props.$enabled ? Theme.white : "#f7f8f8")};
  color: ${Theme.textPrimary};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    border-color: ${Theme.orange} !important;
    background: ${props => (props.$enabled ? "#fff8ef" : Theme.white)} !important;
    color: ${Theme.textPrimary} !important;
    box-shadow: 0 0 0 1px ${Theme.orange};
    transform: translateY(-1px);
  }

  &:active {
    border-color: ${Theme.orangeActive} !important;
    background: #fff3e0 !important;
    transform: translateY(0);
  }

  .ant-btn-icon {
    font-size: 20px;
    color: ${props => (props.$enabled ? Theme.orangeHover : Theme.textTertiary)};
    transition: all 0.2s ease;
  }

  &:hover .ant-btn-icon {
    color: ${Theme.orange};
    transform: scale(1.1);
  }

  ${props =>
    !props.$enabled &&
    `
    opacity: 0.55;
    cursor: default;
    &:hover {
      transform: none !important;
      box-shadow: none !important;
    }
  `}
`;

const ButtonContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-left: 8px;
`;

const ButtonLabel = styled.span`
  font-size: 15px;
  font-weight: 600;
  line-height: 1.3;
  color: ${Theme.textPrimary};
`;

const ButtonDescription = styled.span`
  font-size: 13px;
  color: ${Theme.textTertiary};
  line-height: 1.3;
`;

// ============================================================================
// Provider Icon Map
// ============================================================================

const providerIcons: Record<AuthProvider, React.ReactNode> = {
  cognito: <ToolOutlined />,
  idprism: <ExperimentOutlined />,
  federate: <TeamOutlined />,
};

// ============================================================================
// Component
// ============================================================================

/**
 * Reads the `provider` hint that the edge login flow appends to its post-sign-in redirect.
 *
 * The CloudFront distribution is gated by a Lambda@Edge function, so an unauthenticated visitor is
 * sent to the backend's /auth/login first and chooses their sign-in method there. By the time this
 * page loads they have already chosen, so asking again would be a second, pointless prompt.
 *
 * The backend only ever emits the three known provider literals; anything else is ignored and the
 * normal chooser is shown, so a crafted query parameter cannot send a user somewhere unexpected.
 */
export function getProviderHint(): AuthProvider | null {
  if (typeof window === "undefined") {
    return null;
  }
  const hint = new URLSearchParams(window.location.search).get("provider");
  const match = AUTH_PROVIDERS.find(p => p.provider === hint && p.enabled);
  return match ? match.provider : null;
}

export default function LoginPage() {
  const [messageApi, contextHolder] = message.useMessage();
  const [autoSigningIn, setAutoSigningIn] = useState(() => getProviderHint() !== null);

  const handleProviderClick = async (provider: AuthProvider, enabled: boolean) => {
    if (!enabled) {
      messageApi.info("This login method is coming soon.");
      return;
    }

    try {
      await login(provider);
    } catch (error) {
      messageApi.error(`Login failed: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  };

  // Honour the provider hint exactly once. The ref guard matters rather than being belt-and-braces:
  // login() generates a PKCE verifier and state, stores them in sessionStorage, then navigates. Two
  // overlapping calls can interleave those writes and leave a verifier that does not match the
  // state the provider is given, which fails at the callback. An empty dependency array is not
  // enough on its own, because React Strict Mode intentionally double-invokes effects in
  // development.
  const autoSignInStarted = useRef(false);

  useEffect(() => {
    if (autoSignInStarted.current) {
      return;
    }
    const provider = getProviderHint();
    if (!provider) {
      return;
    }
    autoSignInStarted.current = true;

    // If it fails we drop back to the chooser rather than leaving the user on a spinner with no
    // way forward.
    login(provider).catch(error => {
      setAutoSigningIn(false);
      messageApi.error(`Login failed: ${error instanceof Error ? error.message : "Unknown error"}`);
    });
  }, [messageApi]);

  if (autoSigningIn) {
    return (
      <LoginContainer>
        {contextHolder}
        <Spin size="large" tip="Signing you in…" />
      </LoginContainer>
    );
  }

  return (
    <LoginContainer>
      {contextHolder}

      {/* Top bar — consistent with MainHeader */}
      <TopBar>
        <TopBarLogo>
          <Image width="6rem" preview={false} src={amzLogo} />
          <span>RadarPlus</span>
        </TopBarLogo>
      </TopBar>

      <LoginCard>
        <CardHeader>
          <Title level={3} style={{ color: Theme.white, marginBottom: 4, fontWeight: 700 }}>
            Welcome to RadarPlus
          </Title>
          <Text style={{ color: Theme.textWhiteMuted, fontSize: 13 }}>Device Testing Radar Platform</Text>
        </CardHeader>
        <Divider />
        <CardBody>
          <Text style={{ display: "block", marginBottom: 20, color: Theme.textSecondary, fontSize: 14 }}>Select your login method to continue</Text>

          <Space direction="vertical" size={12} style={{ width: "100%" }}>
            {AUTH_PROVIDERS.map(provider => (
              <ProviderButton key={provider.provider} type="default" icon={providerIcons[provider.provider]} $enabled={provider.enabled} onClick={() => handleProviderClick(provider.provider, provider.enabled)}>
                <ButtonContent>
                  <ButtonLabel>
                    {provider.name}
                    {!provider.enabled && (
                      <Tag
                        color="default"
                        style={{
                          marginLeft: 8,
                          fontSize: 10,
                          lineHeight: "16px",
                          borderRadius: 4,
                          border: `1px solid ${Theme.border}`,
                        }}
                      >
                        Coming Soon
                      </Tag>
                    )}
                  </ButtonLabel>
                  <ButtonDescription>{provider.description}</ButtonDescription>
                </ButtonContent>
              </ProviderButton>
            ))}
          </Space>
        </CardBody>
      </LoginCard>
    </LoginContainer>
  );
}
